import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Subscribes to the cross-tenant admin monitoring WebSocket stream.
 *
 * Endpoint: /api/v1/admin/monitoring/stream?token=<JWT>
 *
 * The hook is intentionally separate from the user-facing
 * `services/websocket.js` singleton: the monitoring stream is a
 * different endpoint with a different message shape and a different
 * authorization model (admin/superadmin only). Sharing the singleton
 * would force one panel to fight the other for connection state.
 *
 * Usage:
 *   const { events, status, lastEvent } = useMonitoringWebSocket(handler);
 *   // handler(event) is invoked once per inbound message
 */
export default function useMonitoringWebSocket(onEvent) {
  const [status, setStatus] = useState('idle'); // idle | connecting | open | closed | error
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const onEventRef = useRef(onEvent);

  // Keep the latest handler in a ref so reconnects don't re-run on
  // every render where the parent passes a fresh closure.
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setStatus('error');
      return;
    }

    // The dev server proxies /api/v1 to the backend, including
    // websocket upgrade. In production REACT_APP_API_URL points at
    // the API origin; we derive ws/wss from window.location for the
    // dev proxy case so a custom port still works.
    const apiUrl =
      process.env.NODE_ENV === 'production'
        ? process.env.REACT_APP_API_URL || ''
        : '';
    let wsUrl;
    if (apiUrl) {
      wsUrl = apiUrl.replace(/^http/, 'ws');
    } else {
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      wsUrl = `${proto}://${window.location.host}`;
    }
    const url = `${wsUrl}/api/v1/admin/monitoring/stream?token=${encodeURIComponent(
      token
    )}`;

    setStatus('connecting');
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      console.error('Failed to open monitoring WS:', err);
      setStatus('error');
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus('open');
    };
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        // Server-side keepalive: reply to ping with pong.
        if (msg && msg.type === 'ping') {
          try {
            ws.send('pong');
          } catch (_) {
            /* ignore */
          }
          return;
        }
        setLastEvent(msg);
        if (onEventRef.current) {
          onEventRef.current(msg);
        }
      } catch (err) {
        console.warn('Monitoring WS: malformed message', evt.data);
      }
    };
    ws.onclose = () => {
      setStatus('closed');
      // Reconnect after a delay; backoff is intentionally simple
      // because the connection is light-weight and admins refreshing
      // a tab shouldn't see "disconnected" linger.
      reconnectTimer.current = setTimeout(() => {
        connect();
      }, 5000);
    };
    ws.onerror = (err) => {
      console.warn('Monitoring WS error', err);
      setStatus('error');
    };
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (_) {
          /* ignore */
        }
        wsRef.current = null;
      }
    };
    // ``connect`` is stable; effect runs once.
  }, [connect]);

  return { status, lastEvent };
}
